<template>
  <ion-page>
    <ion-content
      :class="['main-background', 'today-content', 'today-showcase-bg']"
      :fullscreen="true"
      :scroll-y="true"
    >
      <ion-refresher slot="fixed" @ionRefresh="onRefresh">
        <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
      </ion-refresher>

      <div class="today-hero ion-padding">
        <div class="today-hero-glow" aria-hidden="true" />
        <div class="today-hero-inner">
          <div class="today-brand">
            <img src="@/theme/liga-logo-white.png" alt="Национальная Лига Тренеров" class="today-brand-logo" />
            <div class="today-title-wrap">
              <h1 class="today-hero-title">Сегодня</h1>
              <p class="today-hero-sub">{{ now }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- События -->
      <section
        class="today-section today-section-events"
        v-if="!globalContext.offlineMode && isAuthenticated"
      >
        <div class="today-section-head">
          <span class="today-section-accent" aria-hidden="true" />
          <h2 class="today-section-title">События</h2>
        </div>
        <div ref="eventsScrollWrap" class="today-scroll-wrap">
          <div class="today-scroll">
            <article
              v-for="ev in upcomingEvents"
              :key="ev.id"
              class="today-event-card"
              @click="$router.push(`/events/${ev.id}`)"
            >
              <div class="today-event-image-wrap">
                <img
                  :src="ev.logoPath ? eventsService.logoUrl(ev.logoPath, 480) : fallbackLogo"
                  :alt="ev.title"
                  class="today-event-img"
                  loading="lazy"
                />
                <div class="today-event-overlay">
                  <h3 class="today-event-title">{{ ev.title }}</h3>
                  <p class="today-event-meta">{{ formatEventDate(ev.startAt) }}</p>
                  <p class="today-event-loc">{{ ev.location || 'Место уточняется' }}</p>
                  <div class="today-event-badges">
                    <span v-if="ev.isPaid" class="today-badge today-badge--paid">Платное</span>
                    <span v-if="isSoldOut(ev)" class="today-badge today-badge--soldout">Sold out</span>
                    <span v-if="isRegistered(ev)" class="today-badge today-badge--registered">Записан</span>
                  </div>
                </div>
              </div>
              <div
                v-if="ev.isPaid && !isSoldOut(ev)"
                class="today-event-actions"
                @click.stop
              >
                <ion-button
                  expand="block"
                  class="today-buy-btn today-accent-btn"
                  fill="solid"
                  size="small"
                  @click="onPaidEventAction(ev)"
                >
                  Оформить участие
                </ion-button>
              </div>
            </article>
            <div
              v-for="n in (upcomingEvents.length === 0 && isLoadingEvents ? 4 : 0)"
              :key="'event-skeleton-' + n"
              class="today-event-card today-event-card--skeleton"
            >
              <ion-skeleton-text :animated="true" class="today-event-skeleton"></ion-skeleton-text>
            </div>
          </div>
        </div>
        <div class="today-footer-link" v-if="upcomingEvents.length > 0">
          <router-link to="/events">Все события →</router-link>
        </div>
        <div v-else-if="!isLoadingEvents" class="today-empty">
          <p class="today-empty-text">Нет предстоящих событий.</p>
          <router-link to="/events" class="today-empty-link">Перейти к событиям</router-link>
        </div>
      </section>

      <!-- Программы -->
      <section class="today-section" v-if="!globalContext.offlineMode">
        <div class="today-section-head">
          <span class="today-section-accent today-section-accent--orange" aria-hidden="true" />
          <h2 class="today-section-title">Программы</h2>
        </div>
        <div class="today-scroll-wrap">
          <div class="today-scroll">
            <button
              v-for="program in availablePrograms"
              :key="program.shortName"
              type="button"
              class="today-program-tile"
              @click="navigateToProgram(program)"
            >
              <div class="today-program-cover">
                <div v-if="!programLogos[program.shortName] && isLoadingLogos" class="today-program-skeleton-wrap">
                  <ion-skeleton-text :animated="true" style="width: 100%; height: 100%"></ion-skeleton-text>
                </div>
                <img
                  v-else
                  :src="programLogos[program.shortName] || fallbackLogo"
                  :alt="program.name"
                  class="today-program-img"
                  @load="onProgramImageLoad(program.shortName)"
                  @error="onProgramImageError(program.shortName)"
                />
              </div>
              <span class="today-program-name">{{ program.name }}</span>
              <span class="today-program-desc">{{ program.description }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- Тренинги -->
      <section class="today-section today-section--last" v-if="!globalContext.offlineMode">
        <div class="today-section-head">
          <span class="today-section-accent" aria-hidden="true" />
          <h2 class="today-section-title">Тренинги</h2>
        </div>
        <p v-if="!isLoadingShowcase && educationSectionsVisible.length === 0" class="today-empty-inline">
          Пока нет разделов
        </p>
        <div v-else-if="educationSectionsVisible.length > 0" class="today-scroll-wrap">
          <div class="today-scroll">
            <button
              v-for="sec in educationSectionsVisible"
              :key="'edu-' + sec.folderKey"
              type="button"
              class="today-edu-tile"
              @click="onEducationSectionClick(sec)"
            >
              <span class="today-edu-icon" aria-hidden="true">
                <ion-icon :icon="schoolOutline" />
              </span>
              <span class="today-edu-title">{{ sec.name }}</span>
              <span v-if="(sec.videos || []).length" class="today-edu-meta">
                {{ (sec.videos || []).length }} {{ materialsCountLabel((sec.videos || []).length) }}
              </span>
            </button>
          </div>
        </div>
        <div v-else class="today-scroll-wrap">
          <div class="today-scroll">
            <div
              v-for="n in 3"
              :key="'edu-skel-' + n"
              class="today-edu-tile today-edu-tile--skeleton"
            >
              <ion-skeleton-text :animated="true" class="today-edu-skel-icon" />
              <ion-skeleton-text :animated="true" class="today-edu-skel-line" />
              <ion-skeleton-text :animated="true" class="today-edu-skel-meta" />
            </div>
          </div>
        </div>
      </section>

      <div v-if="globalContext.offlineMode" class="today-offline ion-padding">
        <div class="today-offline-panel">
          <ion-icon :icon="cloudOffline" class="today-offline-icon" />
          <h3 class="today-offline-title">Режим офлайн</h3>
          <ion-button class="today-reconnect-btn today-accent-btn" @click="reconnect()" :disabled="reconnecting">
            <ion-icon slot="icon-only" :icon="refreshOutline" v-show="!reconnecting"></ion-icon>
            <ion-spinner name="bubbles" v-show="reconnecting"></ion-spinner>
          </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>


<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import AccountService from "@/services/account.service";
import LogoService from "@/services/logo.service";
import store from '@/store/index';
import { CapacitorHttp } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  IonSpinner,
  toastController
} from "@ionic/vue";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {
  calendarOutline,
  cloudOffline,
  refreshOutline,
  schoolOutline
} from "ionicons/icons";
import { defineComponent, inject } from "vue";

// import required modules
import { ILigaEvent } from "@/model/liga-event.model";
import eventsService from "@/services/events.service";
import { fetchPublicShowcase, type PublicShowcaseEducationSection } from "@/services/showcase.service";

export default defineComponent({
  props: {
    timeout: { type: Number, default: 1000 },
  },
  name: "Today",
  components: {
    IonContent,
    IonPage,
    IonIcon,
    IonSpinner,
    IonButton,
    IonRefresher,
    IonRefresherContent,
    IonSkeletonText,
  },

  setup() {
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;
    const now = dayjs().format("dd DD.MM.YYYY");
    const logoService = new LogoService();

    return {
      globalContext,
      refreshOutline,
      cloudOffline,
      schoolOutline,
      isMobile,
      now,
      logoService,
    };
  },
  data() {
    return {
      reconnecting: false,
      accountService: new AccountService(),
      isLoadingLogos: false,
      upcomingEvents: [] as ILigaEvent[],
      isLoadingEvents: false,
      /** id событий, на которые пользователь записан (для пина «Записан»). */
      registeredEventIds: [] as number[],
      eventsService,
      calendarOutline,
      programLogos: {} as Record<string, string>,
      fallbackLogo: require('@/theme/liga-logo-white.png'),
      educationSections: [] as PublicShowcaseEducationSection[],
      isLoadingShowcase: false,
      availablePrograms: [
        { shortName: 'HD', name: 'Hypedance', description: 'Танцевальные тренировки' },
        { shortName: 'FB', name: 'Functional Ballet', description: 'Функциональный балет' },
        { shortName: 'PP', name: 'Power Pump', description: 'Силовая тренировка' },
        { shortName: 'PLS', name: 'Pilates', description: 'Пилатес' },
        { shortName: 'LSTR', name: 'Liga Stretch', description: 'Стретчинг' },
        { shortName: 'WORKSHOP', name: 'Workshop', description: 'Мастер-классы' },
      ],
    };
  },

  ionViewWillEnter() {
    if (!this.globalContext.offlineMode) {
      this.loadProgramLogos();
      this.loadShowcaseEducation();
      this.loadEvents();
    } else {
      this.loadProgramLogos();
    }
  },

  ionViewDidEnter() {
    if (!this.globalContext.offlineMode) {
      this.loadProgramLogos();
      this.loadShowcaseEducation();
      this.loadEvents();
    } else {
      this.loadProgramLogos();
    }
  },

  computed: {
    isAuthenticated: () => {
      return store.getters.authenticated;
    },
    educationSectionsVisible(): PublicShowcaseEducationSection[] {
      return (this.educationSections || []).filter((s) => (s.videos || []).length > 0);
    }
  },

  watch: {
    isAuthenticated: {
      handler(val) {
        if (val && !this.globalContext.offlineMode) {
          this.loadShowcaseEducation();
          this.loadEvents();
        }
      },
      immediate: true
    },
    '$route.name'(name) {
      if (name === 'Today' && !this.globalContext.offlineMode) {
        this.loadProgramLogos();
        this.loadShowcaseEducation();
        this.loadEvents();
      } else if (name === 'Today') {
        this.loadProgramLogos();
      }
    }
  },

  methods: {
    async loadProgramLogos() {
      this.isLoadingLogos = true;
      const loadPromises = this.availablePrograms.map(async (program) => {
        try {
          const logoUrl = await this.logoService.getProgramLogo(program.shortName);
          this.programLogos[program.shortName] = logoUrl;
        } catch (error) {
          console.error(`Failed to load logo for ${program.shortName}:`, error);
          this.programLogos[program.shortName] = this.fallbackLogo;
        }
      });
      
      await Promise.allSettled(loadPromises);
      this.isLoadingLogos = false;
    },

    onProgramImageLoad() {
      // Image loaded successfully
    },

    onProgramImageError(shortName: string) {
      // Fallback to default logo on error
      this.programLogos[shortName] = this.fallbackLogo;
    },

    navigateToProgram(program?: { name: string; shortName: string }) {
      if (!program?.shortName) {
        this.$router.push('/programs');
        return;
      }
      this.$router.push({ name: 'ProgramReleases', params: { shortName: program.shortName } });
    },

    onRefresh(refreshEvent?: CustomEvent) {
      if (refreshEvent?.target) {
        this.loadProgramLogos();
        this.loadShowcaseEducation();
        this.loadEvents();
        (refreshEvent.detail as { complete: () => void }).complete();
      }
    },

    materialsCountLabel(n: number): string {
      const v = n % 10;
      const v2 = n % 100;
      if (v2 >= 11 && v2 <= 14) return "материалов";
      if (v === 1) return "материал";
      if (v >= 2 && v <= 4) return "материала";
      return "материалов";
    },

    async loadShowcaseEducation() {
      this.isLoadingShowcase = true;
      try {
        const data = await fetchPublicShowcase();
        this.educationSections = data.educationSections || [];
      } catch (e) {
        console.error("today showcase education", e);
        this.educationSections = [];
      } finally {
        this.isLoadingShowcase = false;
      }
    },

    async toast(message: string, color = "medium") {
      const t = await toastController.create({ message, duration: 2800, position: "top", color });
      await t.present();
    },

    async onEducationSectionClick(sec: PublicShowcaseEducationSection) {
      if (!this.isAuthenticated) {
        await this.toast("Войдите в личный кабинет, чтобы открыть раздел", "warning");
        return;
      }
      try {
        const { data: sections } = await axios.get<Array<{ folderKey: string }>>("api/admin/education/sections");
        const keys = new Set((sections || []).map((s) => s.folderKey));
        if (!keys.has(sec.folderKey)) {
          await this.toast("Нет доступа к этому разделу", "warning");
          return;
        }
        await this.$router.push({
          path: "/skills",
          query: { section: sec.folderKey }
        });
      } catch (e) {
        console.error("education access", e);
        await this.toast("Не удалось проверить доступ", "danger");
      }
    },

    isSoldOut(ev: import('@/model/liga-event.model').ILigaEvent): boolean {
      if (ev.maxRegistrations == null) return false;
      const count = ev.registrationsCount ?? 0;
      return count >= ev.maxRegistrations;
    },

    isRegistered(ev: import('@/model/liga-event.model').ILigaEvent): boolean {
      return this.registeredEventIds.includes(ev.id);
    },

    async loadEvents() {
      if (!this.isAuthenticated) return;
      this.isLoadingEvents = true;
      try {
        const [events, registrations] = await Promise.all([
          eventsService.getEvents(false),
          eventsService.getMyRegistrations()
        ]);
        this.upcomingEvents = events;
        this.registeredEventIds = registrations.map((r) => r.ligaEventId);
      } catch {
        this.upcomingEvents = [];
        this.registeredEventIds = [];
      }
      this.isLoadingEvents = false;
    },

    formatEventDate(iso: string) {
      return dayjs(iso).locale("ru").format("D MMMM, HH:mm");
    },

    onPaidEventAction(ev: ILigaEvent) {
      this.$router.push(`/events/${ev.id}`);
    },
    async reconnect() {
      this.reconnecting = true;
      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      const timeout = setTimeout(() => {
        this.reconnecting = false;
      }, 5000);

      CapacitorHttp.request({
        method: "GET",
        url: `${process.env.VUE_APP_API_URL}api/test-connection`,
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(() => {
          this.globalContext.offlineMode = false;
          this.accountService
            .hasAnyAuthorityAndCheckAuth(process.env.VUE_APP_INSTRUCTOR_ROLE)
            .then((response) => {
              if (response) {
                this.accountService
                  .hasAnyAuthorityAndCheckAuth("ROLE_MANAGER")
                  .then((value) => {
                    this.globalContext.isManager = value;
                    this.$router.replace("/programs");
                  });
              } else {
                this.$router.replace("/login");
              }
            });
        })
        .finally(() => {
          this.reconnecting = false;
          clearTimeout(timeout);
        });
    },

    setOfflineMode() {
      this.globalContext.offlineMode = true;
    },
  },
});
</script>

<style scoped>
.today-content {
  --padding-start: 0;
  --padding-end: 0;
}

.today-showcase-bg {
  --background: linear-gradient(165deg, #151028 0%, #1a1530 35%, #12101f 100%);
}

/* ——— Герой (как витрина) ——— */
.today-hero {
  position: relative;
  padding-top: max(0.75rem, env(safe-area-inset-top));
  padding-bottom: 0.25rem;
  overflow: hidden;
}

.today-hero-glow {
  position: absolute;
  top: -40%;
  right: -20%;
  width: 70%;
  height: 120%;
  background: radial-gradient(ellipse at center, rgba(235, 89, 82, 0.2) 0%, transparent 65%);
  pointer-events: none;
}

.today-hero-inner {
  position: relative;
  z-index: 1;
}

.today-brand {
  display: flex;
  align-items: center;
  gap: 14px;
}

.today-brand-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.35));
}

.today-title-wrap {
  min-width: 0;
}

.today-hero-title {
  margin: 0;
  font-size: clamp(1.35rem, 4vw, 1.85rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #fff;
  line-height: 1.15;
}

.today-hero-sub {
  margin: 0.35rem 0 0 0;
  font-size: 0.88rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
  letter-spacing: 0.02em;
}

/* ——— Секции ——— */
.today-section {
  margin-bottom: 1.75rem;
  padding: 0 4px;
}

.today-section--last {
  margin-bottom: max(2rem, env(safe-area-inset-bottom));
}

/* Зазор от контента выше (герой / тренинги) и зоны FAB до заголовка «События» */
.today-section-events {
  margin-top: 2.25rem;
}

.today-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 12px 14px;
}

.today-section-accent {
  width: 4px;
  height: 22px;
  border-radius: 4px;
  background: linear-gradient(180deg, var(--ion-color-violet-tint) 0%, var(--ion-color-violet) 100%);
  flex-shrink: 0;
}

.today-section-accent--orange {
  background: linear-gradient(180deg, var(--ion-color-orange-tint) 0%, var(--ion-color-orange) 100%);
}

.today-section-title {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.today-scroll-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 6px;
  touch-action: pan-x;
  mask-image: linear-gradient(to right, transparent 0, #000 10px, #000 calc(100% - 10px), transparent 100%);
}

.today-scroll {
  display: flex;
  flex-direction: row;
  gap: 14px;
  padding: 0 14px 8px;
  width: max-content;
}

/* ——— События ——— */
.today-event-card {
  flex: 0 0 auto;
  width: 272px;
  margin: 0;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.08) 0%, rgba(30, 26, 45, 0.95) 100%);
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: transform 0.18s ease;
}

.today-event-card:active {
  transform: scale(0.99);
}

.today-event-card--skeleton {
  height: 220px;
  display: flex;
  align-items: stretch;
  padding: 0;
  cursor: default;
}

.today-event-skeleton {
  width: 100%;
  height: 100%;
  margin: 0;
  border-radius: 20px;
}

.today-event-image-wrap {
  position: relative;
  height: 168px;
  overflow: hidden;
}

.today-event-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.today-event-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 14px 14px;
  background: linear-gradient(transparent 0%, rgba(10, 8, 18, 0.92) 100%);
}

.today-event-title {
  margin: 0 0 4px 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.today-event-meta,
.today-event-loc {
  margin: 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.35;
}

.today-event-badges {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.today-badge {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.today-badge--paid {
  background: rgba(255, 196, 9, 0.95);
  color: #1a1a1a;
}

.today-badge--soldout {
  background: rgba(220, 47, 2, 0.95);
  color: #fff;
}

.today-badge--registered {
  background: rgba(45, 211, 111, 0.9);
  color: #1a1a1a;
}

.today-event-actions {
  padding: 12px 14px 14px;
  background: rgba(8, 6, 16, 0.65);
}

.today-buy-btn {
  margin: 0;
  --border-radius: 12px;
  font-weight: 700;
  text-transform: none;
}

.today-accent-btn {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-shade);
  --color: #fff;
  --box-shadow: 0 6px 20px rgba(235, 89, 82, 0.35);
}

.today-footer-link {
  text-align: center;
  margin-top: 12px;
}

.today-footer-link a {
  color: var(--ion-color-orange-tint);
  font-weight: 600;
  text-decoration: none;
  font-size: 0.95rem;
}

.today-empty {
  text-align: center;
  padding: 1rem 1.25rem 1.5rem;
}

.today-empty-text {
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
}

.today-empty-link {
  color: var(--ion-color-orange-tint);
  font-weight: 600;
  text-decoration: none;
}

.today-empty-inline {
  color: rgba(255, 255, 255, 0.55);
  margin: 0 16px 12px;
  font-size: 0.9rem;
}

/* ——— Тренинги (как на витрине) ——— */
.today-edu-tile {
  flex: 0 0 auto;
  width: 200px;
  margin: 0;
  padding: 18px 16px;
  border: none;
  border-radius: 18px;
  text-align: left;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(71, 82, 114, 0.25) 100%);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform 0.18s ease;
  min-height: 132px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.today-edu-tile:active {
  transform: scale(0.98);
}

.today-edu-tile--skeleton {
  cursor: default;
  pointer-events: none;
}

.today-edu-tile--skeleton:active {
  transform: none;
}

.today-edu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(235, 89, 82, 0.2);
  color: var(--ion-color-orange-tint);
  margin-bottom: 12px;
}

.today-edu-icon ion-icon {
  font-size: 24px;
}

.today-edu-title {
  font-size: 1.02rem;
  font-weight: 700;
  line-height: 1.25;
  color: #fff;
}

.today-edu-meta {
  margin-top: 8px;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 500;
}

.today-edu-skel-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  margin-bottom: 12px;
}

.today-edu-skel-line {
  width: 85%;
  height: 18px;
  border-radius: 8px;
  margin-bottom: 8px;
}

.today-edu-skel-meta {
  width: 55%;
  height: 12px;
  border-radius: 6px;
}

/* ——— Программы ——— */
.today-program-tile {
  flex: 0 0 auto;
  width: 200px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 18px;
  text-align: left;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(71, 82, 114, 0.25) 100%);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  overflow: hidden;
  transition: transform 0.18s ease;
}

.today-program-tile:active {
  transform: scale(0.98);
}

.today-program-cover {
  height: 108px;
  background: linear-gradient(180deg, rgba(71, 82, 114, 0.5) 0%, rgba(0, 0, 0, 0.35) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  position: relative;
}

.today-program-skeleton-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.today-program-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.35));
}

.today-program-name {
  display: block;
  padding: 12px 14px 4px;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25;
}

.today-program-desc {
  display: block;
  padding: 0 14px 16px;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.58);
  line-height: 1.35;
}

/* ——— Офлайн ——— */
.today-offline {
  margin-top: 0.5rem;
}

.today-offline-panel {
  text-align: center;
  padding: 2rem 1.5rem;
  border-radius: 20px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(30, 26, 45, 0.6) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.today-offline-icon {
  font-size: 2.75rem;
  color: var(--ion-color-orange-tint);
  margin-bottom: 0.75rem;
}

.today-offline-title {
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 1.25rem 0;
  letter-spacing: 0.04em;
}

.today-reconnect-btn {
  width: 56px;
  height: 56px;
  --border-radius: 28px;
  margin: 0 auto;
}
</style>